import * as React from 'react';
import { useFormModal } from '@repo/form';
import { FormModal } from '@repo/web-form';
import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { useToast } from '../../../components/ui/Toast';
import { vaultHooks, type PendingHandover } from '../network/vault.api';
import { formatMoney } from '@repo/utils';
import { Decimal } from 'decimal.js';
import { z } from 'zod';

const schema = z.object({
  handoverId: z.string().min(1, 'Select a pending handover'),
  amount: z.string().min(1, 'Counted amount is required'),
  discrepancyReason: z.string().optional(),
});

interface AcceptHandoverModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Staff acceptance of an agent-submitted cash handover (Lane A): the expected amount is
 * system-computed from the agent's held receipts — staff only enter what was actually
 * counted. A mismatch demands a written explanation before the handover can be accepted,
 * at which point the staged receipts are promoted and the vault balance moves.
 */
export function AcceptHandoverModal({ open, onClose }: AcceptHandoverModalProps) {
  const { toast } = useToast();
  const acceptMutation = vaultHooks.useAcceptHandover();

  const { data: pendingData, isLoading: pendingLoading } = vaultHooks.useHandovers(open ? 'pending' : undefined);
  const pending = React.useMemo(() => (Array.isArray(pendingData) ? pendingData : (pendingData?.data ?? [])), [pendingData]);
  const byId = React.useMemo(() => new Map(pending.map((h) => [h.id, h])), [pending]);

  const modal = useFormModal(
    { defaultValues: { handoverId: '', amount: '', discrepancyReason: '' }, schema },
    acceptMutation,
    {
      onBeforeSubmit: (value) => {
        const handover: PendingHandover | undefined = byId.get(value.handoverId);
        if (!handover) throw new Error('Please select a pending handover');

        const actualAmountPaise = new Decimal(value.amount || 0).mul(100).round().toNumber();
        if (actualAmountPaise < 0) throw new Error('Counted amount cannot be negative');

        const expectedAmountPaise = handover.expectedAmountPaise ?? 0;
        const discrepancyPaise = actualAmountPaise - expectedAmountPaise;
        if (discrepancyPaise !== 0 && !value.discrepancyReason?.trim()) {
          throw new Error(
            `Counted amount vs expected ${formatMoney(expectedAmountPaise)} differs by ${formatMoney(Math.abs(discrepancyPaise))} — an explanation is mandatory`,
          );
        }

        return {
          handoverId: handover.id,
          actualAmountPaise,
          discrepancyReason: value.discrepancyReason?.trim() || undefined,
        };
      },
      onSuccess: () => {
        toast({ type: 'success', title: 'Handover Accepted', description: 'Cash received and held receipts confirmed.' });
        onClose();
      },
      onError: (e) => {
        toast({ type: 'error', title: 'Error', description: e?.message ?? 'Failed to accept handover' });
      },
    },
  );

  React.useEffect(() => {
    if (open) {
      modal.form.reset();
    }
  }, [open]);

  return (
    <FormModal open={open} onClose={onClose} title="Accept Agent Handover" modal={modal} submitLabel="Accept Handover">
      <modal.form.Field
        name="handoverId"
        validators={{
          onChange: ({ value }: { value: string }) => (!value ? 'Select a pending handover' : undefined),
        }}
        children={(field: any) => (
          <FormSelect
            label="Pending Handover"
            required
            options={[
              { value: '', label: pendingLoading ? 'Loading...' : 'Select a handover...' },
              ...pending.map((h) => ({
                value: h.id,
                label: `${h.agentName ?? h.agentId} — expected ${formatMoney(h.expectedAmountPaise ?? 0)}`,
              })),
            ]}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            error={modal.fieldError(field.state.meta)}
          />
        )}
      />

      <modal.form.Field
        name="amount"
        validators={{
          onChange: ({ value }: { value: any }) => {
            if (!value) return 'Counted amount is required';
            const isValid = /^\d+(\.\d{1,2})?$/.test(value);
            return isValid ? undefined : 'Valid amount is required';
          },
        }}
        children={(field: any) => (
          <FormField
            label="Counted Amount"
            required
            error={modal.fieldError(field.state.meta)}
          >
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              startIcon={<span className="text-text-muted">INR</span>}
            />
          </FormField>
        )}
      />

      <modal.form.Field
        name="discrepancyReason"
        children={(field: any) => (
          <FormField
            label="Discrepancy Explanation"
            hint="Required if the counted amount differs from the expected total."
          >
            <Input
              placeholder="Only needed when amounts differ"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
          </FormField>
        )}
      />
    </FormModal>
  );
}
